import { useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Inbox } from 'lucide-react';
import {
  useAdminCreatorRequests,
  useAdminTeams,
  useAdminNiches,
  useAdminRequesterDirectory,
  useAllAdminInviters,
} from '@/hooks/useAdmin';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Pill, toneFor } from '@/components/ui/pill';
import { PlatformIcon } from '@/components/PlatformIcon';
import { Tag, channelToneFor, channelLabel } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import { FilterField } from '@/components/ui/filter-field';
import { Pagination } from '@/components/ui/pagination';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { ActiveFilters, type ActiveFilter } from '@/components/ui/active-filters';
import { firstNameOf } from '@/lib/displayName';
import { requestStatusLabel, teamLabel } from '@/lib/labels';
import { daysSince, formatDate, formatDateTime } from '@/lib/dates';
import {
  ALL_REQUEST_STATUSES,
  expandRequestStatusFilter,
  formatDayCount,
} from '@/lib/peoplePages';
import { useUrlState } from '@/hooks/useUrlState';
import { useMainScopeLabel, useTeamScope } from '@/hooks/useTeamScope';
import { INVITE_CHANNELS } from '@/api/types';

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open (not finished)' },
  { value: 'all', label: 'All statuses' },
  ...ALL_REQUEST_STATUSES.map((s) => ({ value: s, label: requestStatusLabel(s) })),
];

function statusFilterLabel(value: string): string {
  return STATUS_OPTIONS.find((o) => o.value === value)?.label ?? requestStatusLabel(value);
}

export function AdminCreatorRequests(): JSX.Element {
  const { team, locked, params: teamParams } = useTeamScope();
  const mainScopeLabel = useMainScopeLabel();
  // Main view: Influencer and Vyral requests are left out (own sections).
  const excludeTeam = teamParams.excludeTeam;
  // `team` is the same URL param useTeamScope reads; it's declared here too so
  // a team change and its dependent resets land in ONE navigation (two
  // setSearchParams calls in a row clobber each other).
  const [f, setF] = useUrlState({
    team: '',
    status: 'open',
    niche: '',
    channel: '',
    cm: '',
    page: 1,
  });
  const [showMore, setShowMore] = useState(Boolean(f.niche || f.channel || f.cm));

  const teams = useAdminTeams();
  const niches = useAdminNiches();
  // The campaign manager directory narrows to the team in scope, so the
  // picker only lists people whose requests target that team.
  const requesters = useAdminRequesterDirectory(team || undefined, excludeTeam);
  const inviters = useAllAdminInviters(team ? { team } : {});
  const statusParam = expandRequestStatusFilter(f.status);
  const query = useAdminCreatorRequests({
    ...(statusParam ? { status: statusParam } : {}),
    ...(team ? { targetTeam: team } : {}),
    ...(excludeTeam ? { excludeTeam } : {}),
    ...(f.niche ? { niche: f.niche } : {}),
    ...(f.channel ? { channel: f.channel } : {}),
    ...(f.cm ? { requesterExternalId: f.cm } : {}),
    page: f.page,
    limit: PAGE_SIZE,
  });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  const teamNameByCode = useMemo(
    () => new Map<string, string>((teams.data ?? []).map((t) => [t.code, t.displayName])),
    [teams.data],
  );
  const nicheNameByCode = useMemo(
    () => new Map<string, string>((niches.data ?? []).map((n) => [n.code, n.displayName])),
    [niches.data],
  );
  const inviterNameById = useMemo(
    () => new Map<number, string>((inviters.data ?? []).map((i) => [i.id, i.name])),
    [inviters.data],
  );
  const requesterNameById = useMemo(
    () =>
      new Map<string, string>(
        (requesters.data ?? []).map((r) => [
          r.requesterExternalId,
          firstNameOf(r.requesterName) || r.requesterName,
        ]),
      ),
    [requesters.data],
  );
  const nicheName = (code: string): string => nicheNameByCode.get(code) ?? code;

  const activeFilters: ActiveFilter[] = [
    f.status !== 'open'
      ? {
          key: 'status',
          label: `Status: ${statusFilterLabel(f.status)}`,
          clear: () => setF({ status: 'open', page: 1 }),
        }
      : null,
    team && !locked
      ? {
          key: 'team',
          label: `Team: ${teamLabel(team, teamNameByCode.get(team))}`,
          clear: () => setF({ team: '', cm: '', page: 1 }),
        }
      : null,
    f.cm
      ? {
          key: 'cm',
          label: `Campaign manager: ${requesterNameById.get(f.cm) ?? 'selected'}`,
          clear: () => setF({ cm: '', page: 1 }),
        }
      : null,
    f.niche
      ? { key: 'niche', label: `Niche: ${nicheName(f.niche)}`, clear: () => setF({ niche: '', page: 1 }) }
      : null,
    f.channel
      ? {
          key: 'channel',
          label: `Channel: ${channelLabel(f.channel)}`,
          clear: () => setF({ channel: '', page: 1 }),
        }
      : null,
  ].filter((x): x is ActiveFilter => x !== null);

  function clearAllFilters(): void {
    setF({
      status: 'open',
      niche: '',
      channel: '',
      cm: '',
      page: 1,
      ...(locked ? {} : { team: '' }),
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Creator requests"
        description="Creators and niches campaign managers have asked for. Assign one to an outreacher and move it along until it's rostered."
      />
      {excludeTeam && (
        <p className="text-xs text-muted-foreground">
          Influencer requests live under{' '}
          <Link
            to="/admin/influencer/requests"
            className="font-medium text-foreground/80 underline-offset-4 hover:text-foreground hover:underline"
          >
            Influencer → Creator requests
          </Link>
        </p>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="Status">
            <Combobox
              className="w-full md:w-52"
              value={f.status}
              onChange={(v) => setF({ status: v || 'all', page: 1 })}
              options={STATUS_OPTIONS}
              placeholder="Open (not finished)"
            />
          </FilterField>
          {!locked && (
            <FilterField label="Team">
              <Combobox
                className="w-full md:w-44"
                value={team}
                // The selected campaign manager may have nothing on the new team.
                onChange={(v) => setF({ team: v, cm: '', page: 1 })}
                options={(teams.data ?? [])
                  .filter((t) => t.isActive)
                  .map((t) => ({ value: t.code, label: t.displayName }))}
                allowAll
                allLabel={mainScopeLabel}
                placeholder={mainScopeLabel}
              />
            </FilterField>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowMore((v) => !v)}
            aria-expanded={showMore}
            aria-controls="requests-more-filters"
            className="text-muted-foreground hover:text-foreground"
          >
            {showMore ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showMore ? 'Fewer filters' : 'More filters'}
          </Button>
        </div>

        {showMore && (
          <div id="requests-more-filters" className="flex flex-wrap items-end gap-3 border-t pt-3">
            <FilterField label="Campaign manager">
              <Combobox
                className="w-full md:w-56"
                value={f.cm}
                onChange={(v) => setF({ cm: v, page: 1 })}
                options={(requesters.data ?? []).map((r) => ({
                  value: r.requesterExternalId,
                  label: `${firstNameOf(r.requesterName) || r.requesterName} (${r.requestCount})`,
                  hint: r.requesterEmail,
                }))}
                allowAll
                allLabel="Any campaign manager"
                placeholder="Any campaign manager"
              />
            </FilterField>
            <FilterField label="Niche">
              <Combobox
                className="w-full md:w-56"
                value={f.niche}
                onChange={(v) => setF({ niche: v, page: 1 })}
                options={(niches.data ?? []).map((n) => ({ value: n.code, label: n.displayName }))}
                allowAll
                allLabel="Any niche"
                placeholder="Any niche"
              />
            </FilterField>
            <FilterField label="Channel">
              <Combobox
                className="w-full md:w-40"
                value={f.channel}
                onChange={(v) => setF({ channel: v, page: 1 })}
                options={INVITE_CHANNELS.map((c) => ({ value: c, label: channelLabel(c) }))}
                allowAll
                allLabel="Any channel"
                placeholder="Any channel"
              />
            </FilterField>
          </div>
        )}

        <ActiveFilters filters={activeFilters} onClearAll={clearAllFilters} />
      </div>

      {query.isLoading ? (
        <TableSkeleton cols={['w-40', 'w-16', 'w-16', 'w-16', 'w-16', 'w-16', 'w-16', 'w-16']} />
      ) : query.error ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn't load creator requests: {(query.error as Error).message}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title={f.status === 'open' ? 'No open creator requests.' : 'No requests match.'}
          hint={
            f.status === 'open'
              ? 'Everything campaign managers asked for is finished. New requests land here as they come in.'
              : 'Try clearing a filter.'
          }
        />
      ) : (
        <DataTable aria-label="Creator requests">
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Target</DataTableHeaderCell>
              <DataTableHeaderCell>Team</DataTableHeaderCell>
              <DataTableHeaderCell>Channel</DataTableHeaderCell>
              <DataTableHeaderCell>Status</DataTableHeaderCell>
              <DataTableHeaderCell>Campaign manager</DataTableHeaderCell>
              <DataTableHeaderCell>Assignee</DataTableHeaderCell>
              <DataTableHeaderCell>Age</DataTableHeaderCell>
              <DataTableHeaderCell>Last activity</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {rows.map((r) => (
              <DataTableRow key={r.id}>
                <DataTableCell anchor>
                  <div className="flex items-center gap-2">
                    {r.requestType === 'CREATOR' && r.channel && (
                      <PlatformIcon platform={r.channel} size={14} className="text-muted-foreground" />
                    )}
                    <Link
                      to={`/admin/creator-requests/${r.publicId}`}
                      className="font-medium text-foreground hover:underline"
                    >
                      {r.requestType === 'CREATOR'
                        ? r.targetIdentifier
                        : r.niches.length > 0
                          ? r.niches.map(nicheName).join(' + ')
                          : 'Niche request'}
                    </Link>
                    {r.requestType === 'NICHE' ? (
                      <Tag tone="niche">Niche</Tag>
                    ) : (
                      <Tag tone="creator">Creator</Tag>
                    )}
                    {r.requestType === 'NICHE' && r.minFollowers ? (
                      <span className="text-[11px] text-muted-foreground">
                        {r.minFollowers.toLocaleString('en-US')}+ followers
                      </span>
                    ) : null}
                  </div>
                </DataTableCell>
                <DataTableCell data-label="Team">
                  <Tag>{teamLabel(r.targetTeam, teamNameByCode.get(r.targetTeam))}</Tag>
                </DataTableCell>
                <DataTableCell data-label="Channel">
                  {r.channel ? (
                    <Tag tone={channelToneFor(r.channel)}>{channelLabel(r.channel)}</Tag>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </DataTableCell>
                <DataTableCell data-label="Status">
                  <Pill tone={toneFor(r.status)}>{requestStatusLabel(r.status)}</Pill>
                </DataTableCell>
                <DataTableCell muted className="text-xs" data-label="Campaign manager">
                  {firstNameOf(r.requesterName) || r.requesterName}
                </DataTableCell>
                <DataTableCell className="text-xs" data-label="Assignee">
                  {r.assignedInviterId ? (
                    inviterNameById.get(r.assignedInviterId) ?? '…'
                  ) : (
                    <span className="text-muted-foreground">Unassigned</span>
                  )}
                </DataTableCell>
                <DataTableCell
                  muted
                  className="text-xs"
                  data-label="Age"
                  title={`Requested ${formatDateTime(r.createdAt)}`}
                >
                  {formatDayCount(daysSince(r.createdAt))}
                  <span className="text-muted-foreground/70"> · {formatDate(r.createdAt)}</span>
                </DataTableCell>
                <DataTableCell
                  muted
                  className="text-xs"
                  data-label="Last activity"
                  title={formatDateTime(r.updatedAt)}
                >
                  {formatDate(r.updatedAt)}
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
      )}

      <Pagination page={f.page} limit={PAGE_SIZE} total={total} onPageChange={(page) => setF({ page })} />
    </div>
  );
}
